import { createRoot } from 'react-dom/client'
import { useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import { useState } from 'react'

import { RegisterUser } from '../lib/api'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { PRETIREMENT_FAVORITED_ACTIONS } from '../lib/constants'
import ConfirmEmailModal from '../components/ConfirmEmailModal'

const SignUp = ({ hero }) => {
  const [favoritedActions, setFavoritedActions] = useLocalStorage(
    PRETIREMENT_FAVORITED_ACTIONS,
    [],
  )
  const [flashMessage, setFlashMessage] = useState('')
  const [flashMessageDescription, setFlashMessageDescription] = useState('')
  const [errorMessage, setErrorMessage] = useState([])
  const [openModal, setOpenModal] = useState(false)

  const {
    register,
    handleSubmit,
    getValues,
    formState: { isDirty, isValid, errors, isSubmitting },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: '',
      firstName: '',
    },
  })

  const onSubmit = async () => {
    setOpenModal(false)
    try {
      const {
        response,
        userData,
        flashMessage,
        flashMessageDescription,
        redirect,
      } = await RegisterUser({
        actions: favoritedActions,
        email: getValues('email'),
        firstName: getValues('firstName'),
      })

      if (redirect) {
        window.location = redirect
      } else if (userData) {
        reset()
        setFavoritedActions([])
        setErrorMessage([])
        setFlashMessage(flashMessage || '')
        setFlashMessageDescription(flashMessageDescription || '')
      } else if (response.status === 400) {
        setFlashMessage('')
        setFlashMessageDescription('')
        setErrorMessage(response.data.errors.email)
      }
    } catch (error) {
      console.log('error', error)
      setFlashMessage('')
      setErrorMessage(['There was an error. Please try again.'])
    }
  }

  const confirmEmail = () => {
    setOpenModal(true)
  }

  return (
    <section className="px-6 lg:px-0 lg:grid lg:grid-cols-breakout">
      <div className="lg:grid lg:grid-cols-6 lg:col-popout-right lg:gap-12 2xl:gap-28">
        <div className="flex flex-col col-span-6 px-6 py-8 mt-10 mb-16 rounded-lg border border-gray-300 md:col-span-3 lg:col-span-2 sm:px-8 md:mb-24 xl:mb-32 lg:border-0">
          {flashMessage ? (
            <div className="mb-4 text-gray-500 headline-md">{flashMessage}</div>
          ) : (
            <h1 className="mb-4 text-gray-500 headline-md">{hero.heroTitle}</h1>
          )}
          <div className="flex flex-col gap-10">
            {flashMessageDescription ? (
              <div
                className="text-gray-500 prose prose-lg md:text-body-lg"
                dangerouslySetInnerHTML={{ __html: flashMessageDescription }}
              />
            ) : (
              <div
                className="text-gray-500 prose prose-lg md:text-body-lg"
                dangerouslySetInnerHTML={{ __html: hero.heroBody }}
              />
            )}

            <div className="w-1/2 md:w-1/3 line line--gray xl:block" />

            {flashMessage ? (
              <div>
                <a href="/" className="w-full btn btn--primary btn--wide">
                  Back to Home
                </a>
              </div>
            ) : (
              <form className="w-full" onSubmit={handleSubmit(confirmEmail)}>
                {errorMessage.length !== 0 && (
                  <ul className="py-2 px-3 rounded-3xl bg-red-500 utility-button-label-sm !leading-normal text-white mb-3  arrow-down arrow-down--red text-center">
                    {errorMessage.map((error, index) => (
                      <li key={index}>
                        {error}
                        {error.includes('already been taken') && (
                          <a href="/log-in" className="underline">
                            &nbsp;Login Here
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mb-8">
                  <label htmlFor="email" className="utility-button-label-md">
                    Your email address
                  </label>
                  <input
                    {...register('email', { required: true })}
                    type="email"
                    id="email"
                    placeholder="name@domain.com"
                    className="mt-3 w-full border border-gray-600 placeholder:text-gray-400 focus:border-blue-800 focus:outline-blue-800 focus:outline-offset-0"
                  />
                  {errors.email && (
                    <span className="mt-2 text-red-500 utility-button-label-sm">
                      A valid email address is required
                    </span>
                  )}
                </div>

                <div className="mb-4">
                  <label htmlFor="name" className="utility-button-label-md">
                    Your first name
                  </label>
                  <input
                    {...register('firstName', { required: true })}
                    type="text"
                    id="name"
                    placeholder="Your First Name"
                    className="mt-3 w-full border border-gray-600 placeholder:text-gray-400 focus:border-blue-800 focus:outline-blue-800 focus:outline-offset-0"
                  />
                  {errors.firstName && (
                    <span className="mt-2 text-red-500 utility-button-label-sm">
                      This field is required
                    </span>
                  )}
                </div>

                <div className="mt-8">
                  <p className="mb-4 text-center text-gray-500 body-sm">
                    (No marketing emails or spam, we promise.)
                  </p>
                  <button
                    type="submit"
                    className="w-full btn btn--primary"
                    disabled={!isDirty || !isValid || isSubmitting}
                  >
                    Sign up
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
        <div className="hidden overflow-hidden relative col-span-6 md:col-span-3 lg:col-span-4 lg:block">
          <img
            className="object-cover w-full h-full"
            src={hero.image.url}
            srcSet={`${hero.image.srcset.medium} 768w, ${hero.image.srcset.large} 1024w, ${hero.image.srcset.xlarge} 1536w`}
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            alt={hero.image.alt}
          />
          <img
            className="absolute inset-0 m-auto w-2/3 max-w-md"
            src={hero.imageOverlay.url}
            alt={hero.imageOverlay.alt}
          />
        </div>
      </div>
      <ConfirmEmailModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        email={getValues('email')}
        onSubmit={onSubmit}
      />
    </section>
  )
}

SignUp.propTypes = {
  hero: PropTypes.shape({
    heroTitle: PropTypes.string.isRequired,
    heroBody: PropTypes.string.isRequired,
    image: {
      url: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
      srcset: PropTypes.shape({
        medium: PropTypes.string.isRequired,
        large: PropTypes.string.isRequired,
        xlarge: PropTypes.string.isRequired,
      }).isRequired,
    },
    imageOverlay: {
      url: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    },
  }).isRequired,
}

export default SignUp

if (document.getElementById('sign-up')) {
  const container = document.getElementById('sign-up')

  const props = Object.assign({}, container.dataset)
  const root = createRoot(container)

  props.hero = JSON.parse(props.hero)

  root.render(<SignUp {...props} />)
}
